import { SHIP_SPEED_PER_HOUR } from '@/api/hooks'
import { PennantIcon } from '@/components/game/GameIcons'

function formatDuration(hours: number): string {
  if (hours <= 0) return '—'
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  return m === 0 ? `${h}h` : `${h}h ${m.toString().padStart(2, '0')}`
}

/**
 * Gréement de course (RacingRig) : +15 % de vitesse de navigation par exemplaire
 * équipé, cumul additif. Miroir de `App\Domain\Ship\ShipModuleEffects::SPEED_PCT`
 * (le serveur reste autoritaire ; ce miroir n'alimente que l'estimation pré-lancement).
 */
const RACING_RIG_SPEED_PCT = 15

type SpeedShip = { name?: string; shipType: string; modules?: { type: string }[] }

/**
 * Effective nautical speed (leagues/h) of ONE ship = base hull speed × its own
 * Gréement de course modules. Mirrors the backend's per-ship effective speed
 * (base `nauticalSpeedPerHour` × `ShipModuleEffects::speedMult`).
 */
function shipEffectiveSpeedPerHour(ship: SpeedShip): number {
  const base = SHIP_SPEED_PER_HOUR[ship.shipType] ?? 6
  const rigs = (ship.modules ?? []).filter((m) => m.type === 'racing_rig').length
  return (base * (100 + RACING_RIG_SPEED_PCT * rigs)) / 100
}

/**
 * Convoy pace (leagues/h) mirroring the backend: the SLOWEST ship's EFFECTIVE
 * speed (base hull × its Gréement de course), then the account's Forge travel-speed
 * multiplier ("Voilure perfectionnée", from `fleet.travelSpeedForgeMult`) applied on
 * top. Both speed sources are folded in, so the pre-launch ETA now reacts to the
 * Gréement de course AND the Forge tech exactly like the server's stored duration.
 * An empty fleet falls back to a nominal 6 leagues/h.
 */
export function convoySpeedPerHour(ships: SpeedShip[], travelForgeMult = 1): number {
  const slowest = ships.length > 0 ? Math.min(...ships.map(shipEffectiveSpeedPerHour)) : 6
  return slowest * (travelForgeMult > 0 ? travelForgeMult : 1)
}

/**
 * Round-trip mission duration (seconds), computed client-side. Mirrors the backend
 * `roundTripSeconds`: Euclidean distance × 2 / effective convoy speed, ×3600, floored
 * at 60s. Callers pass the convoy's effective speed via {@link convoySpeedPerHour}
 * (slowest ship × its module × the Forge travel multiplier), so the estimate matches
 * the server's stored duration; the server stays authoritative at launch.
 */
export function tripDurationSeconds(
  slowestSpeedPerHour: number,
  origin: { x: number; y: number },
  target: { x: number; y: number },
): number {
  if (slowestSpeedPerHour <= 0) return 60
  const oneWay = Math.sqrt((target.x - origin.x) ** 2 + (target.y - origin.y) ** 2)
  return Math.max(60, Math.round(((oneWay * 2) / slowestSpeedPerHour) * 3600))
}

/** Consistent pre-launch ETA line shared across every Map launcher. */
export function TripDuration({ seconds }: { seconds: number }) {
  return <span className="whitespace-nowrap">⏱ ~{formatDuration(seconds / 3600)}</span>
}

/**
 * Le navire qui IMPOSE l'allure du convoi : celui dont la vitesse effective (coque ×
 * ses propres Gréements de course) est la plus basse. À égalité c'est le premier de la
 * sélection, comme côté serveur (`slowestShip` ne change de meneur que sur un `<`
 * strict). null sous deux navires : il n'y a alors rien à expliquer.
 */
export function convoyPaceSetter<T extends SpeedShip>(ships: T[]): T | null {
  if (ships.length < 2) return null

  let pace = ships[0]
  let slowest = shipEffectiveSpeedPerHour(pace)
  for (const ship of ships.slice(1)) {
    const speed = shipEffectiveSpeedPerHour(ship)
    if (speed < slowest) {
      pace = ship
      slowest = speed
    }
  }

  return pace
}

function racingRigs(ship: SpeedShip): number {
  return (ship.modules ?? []).filter((m) => m.type === 'racing_rig').length
}

/**
 * Ce que la coque la plus lente COÛTE au convoi, en secondes : le temps qu'on
 * gagnerait en la laissant à quai. Dérivé du RAPPORT des deux allures plutôt que
 * d'un recalcul de distance, ce qui a deux vertus : le multiplicateur de Forge se
 * simplifie (il pèse sur les deux termes), et le chiffre reste juste même quand la
 * durée vient du serveur et non du miroir client.
 *
 * 0 quand retirer ce navire ne changerait rien, c'est-à-dire quand un autre est
 * aussi lent que lui. Dans ce cas il n'est pas LE fautif, et l'annoncer serait faux.
 */
export function convoyPaceCostSeconds(ships: SpeedShip[], tripSeconds: number): number {
  const pace = convoyPaceSetter(ships)
  if (pace === null || tripSeconds <= 0) return 0

  const others = ships.filter((s) => s !== pace)
  if (others.length === 0) return 0

  const withPace = Math.min(...ships.map(shipEffectiveSpeedPerHour))
  const withoutPace = Math.min(...others.map(shipEffectiveSpeedPerHour))
  if (withoutPace <= withPace) return 0

  return Math.max(0, Math.round(tripSeconds * (1 - withPace / withoutPace)))
}

/**
 * L'allure du convoi, sous l'ETA. Deux niveaux :
 *
 * 1. Une mention permanente qui NOMME la coque imposant le rythme. Le convoi navigue
 *    à l'allure de son navire le plus lent, gréements compris, donc une seule coque
 *    non gréée annule le bonus de toutes les autres. C'est la règle la moins
 *    devinable du jeu, et elle n'était écrite nulle part.
 * 2. Un ruban ambre CHIFFRÉ quand cette coque est la seule à ne pas être gréée alors
 *    que d'autres le sont. Là le capitaine ne perd pas une nuance, il perd des
 *    heures, et le savoir change sa sélection. Muet sur un convoi homogène, muet
 *    aussi quand la coque lente l'est par sa taille et non par son gréement : le
 *    ruban parle de gréement, il ne doit pas mentir sur la cause.
 *
 * Le ruban demande `tripSeconds` pour chiffrer. Sans lui, seule la mention s'affiche.
 */
export function ConvoyPace({ ships, tripSeconds }: { ships: SpeedShip[]; tripSeconds?: number }) {
  const pace = convoyPaceSetter(ships)
  if (pace === null || pace.name === undefined || pace.name === '') return null

  const bare = racingRigs(pace) === 0
  const othersRigged = ships.some((s) => s !== pace && racingRigs(s) > 0)
  const cost = bare && othersRigged && tripSeconds !== undefined ? convoyPaceCostSeconds(ships, tripSeconds) : 0

  return (
    <>
      <p className="text-[11px] text-ocean-400 text-right leading-tight">
        allure imposée par <span className="text-ocean-200">{pace.name}</span>
      </p>
      {cost > 0 && (
        <p className="flex items-start gap-1.5 rounded border border-amber-400/45 bg-amber-400/15 px-2 py-1.5 text-[11px] leading-snug text-amber-300">
          <PennantIcon className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
          <span>
            <strong className="font-semibold text-amber-200">{pace.name}</strong> n&apos;a pas de gréement de course :
            elle coûte <strong className="font-semibold text-amber-200">{formatDuration(cost / 3600)}</strong> au convoi.
          </span>
        </p>
      )}
    </>
  )
}
